import { moneyCentsParam as moneyCentsParam } from '@/shared/filters/dates';
import { msg } from '@lingui/core/macro';

import { defineFilters } from '@/shared/filters/define-filters';
import { defineEntitySource } from '@/shared/form/entity-source';
import { getCurrentTenant } from '@/shared/http/client';

import { BATCH_ASSIGNMENT_STATUS } from './batch-assignment.status';

// Filtros da lista de cessões declarados uma vez (P01; `BatchAssignmentFilters.tsx:20-210`). O cessionário
// chega por uma porta injetada: assim o `public.ts` não puxa o `fund`, que já depende das cessões (sem ciclo).

type FundItem = { readonly id: string; readonly name: string };

/** O que os filtros precisam da porta do `fund` (`fundSource`). */
export interface FundLookup {
  readonly search: (query: string, signal: AbortSignal) => Promise<readonly FundItem[]>;
  readonly byId?: (id: string, signal: AbortSignal) => Promise<FundItem | undefined>;
}

/**
 * `CurrencyFormField` grava centavos (`formatMultiply(valor, 'currency')`), e o filtro manda o número
 * (`filterContext.tsx:131-141`). A URL guarda o texto digitado ("1.000,00").
 */
export { moneyCentsParam } from '@/shared/filters/dates';

const money = { max: 20, toParam: moneyCentsParam, placeholder: msg`Em reais, como 1.000,00` } as const;

/**
 * Na ordem do "Filtrar" do legado, com os rótulos das opções de lá. "Data da cessão" é `date` com
 * `dateRangeName` no legado: o início sai como instante do dia, o fim como `formatFinalDateWithTimeZone`.
 */
export function defineBatchAssignmentListFilters(fund: () => Promise<{ readonly fundSource: FundLookup }>) {
  // Cessionário: a busca do `useFundsList({ page: 0, size: 10 })` do legado, pela porta do `fund`.
  const fundFilterSource = defineEntitySource<FundItem>({
    key: ['batch-assignment', 'filter', 'fund'],
    scope: () => getCurrentTenant() ?? null,
    search: async (query, signal) => (await fund()).fundSource.search(query, signal),
    byId: async (id, signal) => (await fund()).fundSource.byId?.(id, signal),
    toOption: (item) => ({ value: item.id, label: item.name }),
  });
  return defineFilters([
  { key: 'status', type: 'status', label: msg`Status`, options: BATCH_ASSIGNMENT_STATUS, pinned: true },
  { key: 'fundId', type: 'entity', label: msg`Cessionário`, source: fundFilterSource, placeholder: msg`Buscar cessionário` },
  { key: 'initialAssignmentDate', to: 'finalAssignmentDate', type: 'dateRange', label: msg`Data da cessão`, fromLabel: msg`Data inicial`, toLabel: msg`Data final`, pinned: true },
  { key: 'maxAssigmentValue', type: 'text', label: msg`Valor máximo da cessão`, ...money },
  { key: 'minAssigmentValue', type: 'text', label: msg`Valor mínimo da cessão`, ...money },
  { key: 'maxPresentValue', type: 'text', label: msg`Valor máximo presente`, ...money },
  { key: 'minPresentValue', type: 'text', label: msg`Valor mínimo presente`, ...money },
  { key: 'maxAssignmentCalculationValue', type: 'text', label: msg`Valor de cálculo máximo`, ...money },
  { key: 'minAssignmentCalculationValue', type: 'text', label: msg`Valor de cálculo mínimo`, ...money },
  { key: 'creditNoteNos', type: 'text', label: msg`Número da operação`, max: 12 },
  { key: 'assignmentNumber', type: 'text', label: msg`Número da cessão`, max: 12 },
]);
}
